import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, Users, Send, FileCheck, Bell, Wrench, UserCircle2, Percent, BookOpen,
    LogOut, Menu, CalendarClock, ShieldCheck, Gift, Clock, ClipboardCheck, ClipboardList,
    MessageCircle, MessageSquare, BarChart3, Package, HeartHandshake, Radio, IndianRupee,
    ShieldAlert, CalendarCheck, Megaphone, Network, Map, Smartphone, CreditCard, Receipt,
    Wallet, Landmark, MailQuestion, Activity, FileText, FolderKanban, UserCheck, ServerCog,
    ChevronDown, ChevronRight, Search, Sun, Moon, X, Sparkles, TrendingUp, Layers, AlertTriangle, Briefcase,
    Building2, Book,
    Globe,
    Camera,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { allowedNavFor, ROLE_LABELS } from '@/lib/roles';
import LanguageToggle from '@/components/LanguageToggle';
import NotificationBell from '@/components/NotificationBell';
import { LiveShareToggle } from '@/components/LiveShareToggle';
import { UserMenu } from '@/components/UserMenu';
import { BroadcastTicker } from '@/components/BroadcastNews';
import SaathiOnboardingWizard from '@/components/SaathiOnboardingWizard';
import MobileBottomNav from '@/components/MobileBottomNav';
import { useLang } from '@/i18n/LangContext';
import { api } from '@/lib/api';
import { prefetchRoute, prefetchAdminEssentials } from '@/lib/routePrefetch';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ────────────────────────────────────────────────────────────────────────────
// NAV_GROUPS — 7 semantic groups. Each item's `key` matches roles.js so
// admin/staff role filtering flows through unchanged. Groups collapse
// via localStorage-persisted state.
// ────────────────────────────────────────────────────────────────────────────
const NAV_GROUPS = [
    { id: 'overview',  label: 'Overview',           labelHi: 'सारांश',       icon: LayoutDashboard,
        keys: ['dashboard', 'reports', 'landing-cms', 'site-pages', 'blog', 'customer-stories', 'team'] },
    { id: 'crm',       label: 'Sales & CRM',        labelHi: 'बिक्री & CRM', icon: Users,
        keys: ['leads', 'quotations', 'sale-invoices', 'payment-ins', 'payments-report', 'vendor-ledger', 'customer-ledger', 'my-khata', 'customers', 'referrals', 'my-team'] },
    { id: 'ops',       label: 'Operations',         labelHi: 'संचालन',      icon: FolderKanban,
        keys: ['applications', 'projects', 'credit-reports', 'credit-report', 'saathi-bookings', 'complaints', 'sla-config', 'vendors', 'vendor-coverage'] },
    { id: 'payments',  label: 'Payments & Wallet',  labelHi: 'भुगतान & वॉलेट', icon: Wallet,
        keys: ['payment-center', 'wallet', 'my-wallet', 'vendor-wallet', 'bbps', 'bbps-reports', 'razorpay', 'eko-settings', 'redpay-settings', 'provider-router', 'loans', 'subsidy', 'subsidy-tracker'] },
    { id: 'staff',     label: 'Staff & HR',         labelHi: 'स्टाफ & HR',   icon: ShieldCheck,
        keys: ['staff', 'access-bundles', 'care-audit-log', 'vendor-team', 'my-firm', 'hierarchy', 'attendance', 'attendance-summary', 'staff-tracking', 'my-attendance', 'my-attendance-history', 'territory-heatmap'] },
    { id: 'comms',     label: 'Communication',      labelHi: 'संचार',       icon: MessageCircle,
        keys: ['whatsapp-inbox', 'nerochat-logs', 'whatsapp-broadcast', 'wapp-templates', 'view-messages', 'notifications', 'push-console', 'broadcast-news', 'content'] },
    { id: 'config',    label: 'Configuration',      labelHi: 'सेटिंग्स',     icon: ServerCog,
        keys: ['products', 'pending-signups', 'login-events', 'mobile-app', 'deletion-requests', 'action-view'] },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const { t, lang } = useLang();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [staleDeviceCount, setStaleDeviceCount] = useState(0);
    const [bottomNavEnabled, setBottomNavEnabled] = useState(true);
    const [searchQ, setSearchQ] = useState('');
    const searchRef = useRef(null);

    // Sidebar theme: 'dark' (navy) | 'light' (cream) — persisted per user.
    const [sidebarTheme, setSidebarTheme] = useState(() => {
        try { return localStorage.getItem('admin_sidebar_theme') || 'dark'; }
        catch { return 'dark'; }
    });
    const toggleSidebarTheme = () => {
        const next = sidebarTheme === 'dark' ? 'light' : 'dark';
        setSidebarTheme(next);
        try { localStorage.setItem('admin_sidebar_theme', next); } catch { /* ignore */ }
    };
    const isDark = sidebarTheme === 'dark';

    // Theme tokens — one place, all sidebar surfaces reference these.
    const T = isDark ? {
        sidebarBg:  'linear-gradient(180deg,#1E2E7A 0%,#141E5A 100%)',
        color:      '#dfe4f5',
        borderTop:  'rgba(255,255,255,.06)',
        borderCard: 'rgba(255,255,255,.08)',
        inputBg:    'rgba(255,255,255,.06)',
        inputBorder:'rgba(255,255,255,.08)',
        placeholder:'text-white/40',
        text:       'text-white',
        textMuted:  'text-white/70',
        textSubtle: 'text-white/50',
        itemHover:  'hover:bg-white/5 hover:text-white',
        itemInactiveText: 'text-white/75',
        groupLabelText:   'text-white/55',
        groupLabelHover:  'hover:text-white',
        userLabel:  'text-white',
        groupIconBg:'rgba(255,255,255,.10)',
        groupIconColor:'#FDBA74',       // amber-300 pop on navy
        itemIconBg: 'rgba(255,255,255,.08)',
        itemIconColor:'rgba(255,255,255,.9)',
        chevron:    'text-white/60',
        logoutBtn:  'bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white',
        kbdBorder:  'border-white/10',
        kbdText:    'text-white/40',
    } : {
        sidebarBg:  'linear-gradient(180deg,#FEFCF7 0%,#FFF7ED 100%)',
        color:      '#1E2E7A',
        borderTop:  'rgba(30,46,122,.10)',
        borderCard: 'rgba(30,46,122,.12)',
        inputBg:    'rgba(30,46,122,.05)',
        inputBorder:'rgba(30,46,122,.10)',
        placeholder:'placeholder:text-slate-400',
        text:       'text-slate-900',
        textMuted:  'text-slate-600',
        textSubtle: 'text-slate-500',
        itemHover:  'hover:bg-orange-100/70 hover:text-slate-900',
        itemInactiveText: 'text-slate-700',
        groupLabelText:   'text-slate-500',
        groupLabelHover:  'hover:text-slate-800',
        userLabel:  'text-slate-900',
        groupIconBg:'rgba(242,101,34,.12)',
        groupIconColor:'#F26522',
        itemIconBg: 'rgba(30,46,122,.08)',
        itemIconColor:'#1E2E7A',
        chevron:    'text-slate-400',
        logoutBtn:  'bg-white border-slate-200 text-slate-700 hover:bg-orange-50 hover:text-slate-900',
        kbdBorder:  'border-slate-300',
        kbdText:    'text-slate-400',
    };

    // Resolve bottom-nav visibility from admin app-settings
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/me/app-settings/mobile-bottom-nav`);
                if (!cancelled) setBottomNavEnabled(Boolean(data?.enabled));
            } catch { /* keep default */ }
        })();
        return () => { cancelled = true; };
    }, [user]);

    // Mobile-app stale-devices ping (unchanged from prior)
    useEffect(() => {
        if (!user || user.role !== 'admin') return;
        let cancelled = false;
        const load = async () => {
            try {
                const s = await api.adminAppVersionStats(30);
                if (cancelled || !s || !s.buckets || !s.latest_version_code) return;
                const stale = s.buckets
                    .filter(b => b.version_code < s.latest_version_code)
                    .reduce((sum, b) => sum + b.devices, 0);
                setStaleDeviceCount(stale);
            } catch { /* silent */ }
        };
        load();
        const iv = setInterval(load, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [user]);

    // ⌘K / Ctrl+K to focus search
    useEffect(() => {
        const h = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, []);

    // Prefetch the most common admin pages after the app is idle so
    // navigating away from the dashboard feels instant. Runs once on mount.
    useEffect(() => {
        prefetchAdminEssentials();
    }, []);

    const ALL_ITEMS = useMemo(() => ([
        { key: 'dashboard', to: '/admin/dashboard', icon: LayoutDashboard, label: t('dashboard') },
        { key: 'reports', to: '/admin/reports', icon: ClipboardList, label: 'Reports' },
        { key: 'landing-cms', to: '/admin/landing-cms', icon: FileText, label: 'Homepage CMS' },
        { key: 'site-pages', to: '/admin/site-pages', icon: Globe, label: 'Website Pages' },
        { key: 'blog', to: '/admin/blog', icon: BookOpen, label: 'Blog / Knowledge Hub' },
        { key: 'customer-stories', to: '/admin/customer-stories', icon: Camera, label: 'Customer Stories' },
        { key: 'team', to: '/admin/team', icon: Users, label: 'Our Team (Website)' },
        { key: 'leads', to: '/admin/leads', icon: Users, label: t('leads') },
        { key: 'quotations', to: '/admin/quotations', icon: Send, label: t('quotations') },
        { key: 'sale-invoices', to: '/admin/sale-invoices', icon: Receipt, label: 'Sale Invoices' },
        { key: 'payment-ins', to: '/admin/payment-ins', icon: IndianRupee, label: 'Payment-In / Receipts' },
        { key: 'payments-report', to: '/admin/payments-report', icon: BarChart3, label: 'Payment-In Report' },
        { key: 'vendor-ledger', to: '/admin/vendor-ledger', icon: Book, label: 'Vendor Khata Book' },
        { key: 'customer-ledger', to: '/admin/customer-ledger', icon: Book, label: 'Customer Khata Book' },
        { key: 'my-khata', to: '/admin/my-khata', icon: Book, label: 'My Khata Book' },
        { key: 'customers', to: '/admin/customers', icon: UserCircle2, label: t('customers_admin') },
        { key: 'referrals', to: '/admin/referrals', icon: Gift, label: t('referrals') },
        { key: 'my-team', to: '/admin/my-team', icon: Users, label: t('my_team') || 'My Team' },
        { key: 'vendor-team', to: '/admin/vendor-team', icon: Users, label: 'My Team' },
        { key: 'my-firm', to: '/admin/my-firm', icon: Building2, label: 'My Firm Details' },

        { key: 'payment-center', to: '/admin/payment-center', icon: Landmark, label: 'Payment Center' },
        { key: 'applications', to: '/admin/applications', icon: FileCheck, label: t('applications_admin') },
        { key: 'projects', to: '/admin/projects', icon: FolderKanban, label: 'Projects' },
        { key: 'credit-reports', to: '/admin/credit-reports', icon: FileText, label: 'Credit Reports' },
        { key: 'credit-report', to: '/admin/credit-report', icon: FileText, label: 'Pull Credit Report' },
        { key: 'bbps', to: '/admin/bbps', icon: Receipt, label: 'BBPS Bill Pay' },
        { key: 'bbps-reports', to: '/admin/financial-reports', icon: FileText, label: 'Financial Reports' },
        { key: 'wallet', to: '/admin/wallet', icon: Wallet, label: 'Wallet & Ledger' },
        // Phase 23 — Every staff role has an isolated /my-wallet (Wallet + Ledger + Top-up)
        { key: 'my-wallet', to: '/admin/my-wallet', icon: Wallet, label: t('my_wallet') || 'My Wallet' },
        // Phase 20.B — Site Surveys + Saathi Visits merged. The dedicated
        // Site Surveys page is still reachable at /admin/site-surveys but
        // is NOT surfaced in the sidebar anymore; everything now flows
        // through the unified Saathi Bookings page.
        { key: 'saathi-bookings', to: '/admin/saathi-bookings', icon: HeartHandshake, label: t('site_surveys_and_visits') || 'Survey Visits' },
        { key: 'complaints', to: '/admin/complaints', icon: Bell, label: t('complaints') },
        { key: 'sla-config', to: '/admin/sla-config', icon: Clock, label: 'Complaint SLA Config' },
        // Phase 28 — "Vendors" merged into Staff & Roles. Sidebar link removed;
        // /admin/vendors route redirects to /admin/staff?tab=vendor. Kept the
        // key here as a no-op so downstream `allowedNavFor` calls stay stable.
        { key: 'pending-signups', to: '/admin/pending-signups', icon: UserCheck, label: 'Signup Approvals' },
        { key: 'loans', to: '/admin/loans', icon: Landmark, label: 'Apply Loan' },
        { key: 'eko-settings', to: '/admin/eko-settings', icon: ServerCog, label: 'EKO BBPS' },
        { key: 'redpay-settings', to: '/admin/redpay-settings', icon: Wallet, label: 'RedPay' },
        { key: 'provider-router', to: '/admin/provider-router', icon: ServerCog, label: 'Provider Router' },
        { key: 'staff', to: '/admin/staff', icon: ShieldCheck, label: t('staff_roles') },
        { key: 'access-bundles', to: '/admin/access-bundles', icon: Layers, label: 'Access Bundles' },
        { key: 'care-audit-log', to: '/admin/care-audit-log', icon: ShieldCheck, label: 'Care Audit Trail' },
        { key: 'attendance', to: '/admin/attendance', icon: ClipboardCheck, label: t('attendance') },
        { key: 'attendance-summary', to: '/admin/attendance-summary', icon: BarChart3, label: t('attendance_summary') || 'Attendance Report' },
        { key: 'staff-tracking', to: '/admin/staff-tracking', icon: Radio, label: t('staff_tracking') || 'Live Staff Tracking' },
        { key: 'my-attendance', to: '/admin/my-attendance', icon: Clock, label: t('my_attendance') },
        { key: 'products', to: '/admin/products', icon: Package, label: t('products') || 'Product Catalog' },
        { key: 'login-events', to: '/admin/login-events', icon: ShieldAlert, label: t('login_events') || 'Login & Security' },
        { key: 'push-console', to: '/admin/push-console', icon: Radio, label: t('push_console') || 'Push Console' },
        { key: 'broadcast-news', to: '/admin/broadcast-news', icon: Megaphone, label: t('broadcast_news') || 'Broadcast News' },
        { key: 'hierarchy', to: '/admin/hierarchy', icon: Network, label: t('hierarchy') || 'User Hierarchy' },
        { key: 'territory-heatmap', to: '/admin/territory-heatmap', icon: Map, label: 'Territory Heatmap' },
        { key: 'vendor-coverage', to: '/admin/vendor-coverage', icon: Map, label: 'Vendor Coverage Map' },
        { key: 'my-attendance-history', to: '/admin/my-attendance-history', icon: CalendarCheck, label: t('my_attendance_history') || 'My Attendance History' },
        { key: 'subsidy', to: '/admin/subsidy', icon: Percent, label: t('subsidy_rates') },
        { key: 'subsidy-tracker', to: '/admin/subsidy-tracker', icon: IndianRupee, label: 'Subsidy Tracker' },
        { key: 'content', to: '/admin/content', icon: BookOpen, label: t('content_admin') },
        { key: 'career-applications', to: '/admin/career-applications', icon: Briefcase, label: 'Career Applications' },
        { key: 'notifications', to: '/admin/notifications', icon: MessageCircle, label: t('notifications') || 'Notifications' },
        { key: 'view-messages', to: '/admin/view-messages', icon: Send, label: t('view_messages') || 'View Messages' },
        { key: 'whatsapp-inbox', to: '/admin/whatsapp-inbox', icon: MessageSquare, label: t('whatsapp_inbox') || 'WhatsApp Inbox' },
        { key: 'nerochat-logs', to: '/admin/nerochat-logs', icon: Activity, label: t('nerochat_logs') || 'Nerochat API Logs' },
        { key: 'whatsapp-broadcast', to: '/admin/whatsapp-broadcast', icon: Megaphone, label: 'WhatsApp Broadcast' },
        { key: 'wapp-templates', to: '/admin/wapp-templates', icon: FileText, label: 'WhatsApp Templates' },
        { key: 'razorpay', to: '/admin/razorpay', icon: CreditCard, label: 'Razorpay' },
        { key: 'mobile-app', to: '/admin/mobile-app', icon: Smartphone, label: 'Mobile App' },
        { key: 'deletion-requests', to: '/admin/deletion-requests', icon: MailQuestion, label: 'Deletion Requests' },
        { key: 'action-view', to: '/admin/action-view', icon: Activity, label: 'Action View' },
        { key: 'system-errors', to: '/admin/system-errors', icon: AlertTriangle, label: 'System Errors' },
    ]), [t]);

    const allowedKeys = allowedNavFor(user);
    const items = ALL_ITEMS.filter(i => allowedKeys.includes(i.key));

    return (
        <div className='app-shell min-h-screen flex bg-[#FAFAF7] dark:bg-background'>
            {/* Backdrop for mobile drawer */}
            {open && <div className='fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden' onClick={() => setOpen(false)} />}

            <aside
                className={cn(
                    'fixed lg:static inset-y-0 left-0 z-40 w-[272px] flex flex-col transition-transform duration-200 shadow-xl lg:shadow-none',
                    open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
                )}
                style={{ background: T.sidebarBg, color: T.color, borderRight: !isDark ? '1px solid rgba(30,46,122,.10)' : undefined }}
                data-testid='admin-sidebar'
                data-theme={sidebarTheme}
            >
                {/* Brand header — always white so the real UPPower logo blends
                    seamlessly in both dark & light sidebar themes. */}
                <div
                    className='px-4 py-3 flex items-center gap-2 flex-shrink-0'
                    style={{
                        background: '#ffffff',
                        borderBottom: '1px solid rgba(30,46,122,.10)'
                    }}
                >
                    <div className='flex-1 min-w-0'>
                        <img
                            src='/uppower-logo.png'
                            alt='UPPower Solar'
                            className='h-10 sm:h-11 w-auto select-none'
                            draggable='false'
                        />
                    </div>
                    {/* Theme toggle */}
                    <button
                        className='p-1.5 rounded-md transition-colors flex-shrink-0 text-slate-500 hover:text-slate-900 hover:bg-orange-100'
                        onClick={toggleSidebarTheme}
                        data-testid='admin-sidebar-theme-toggle'
                        aria-label={isDark ? 'Switch to light sidebar' : 'Switch to dark sidebar'}
                        title={isDark ? (lang === 'hi' ? 'Light theme पर switch करें' : 'Switch to light theme') : (lang === 'hi' ? 'Dark theme पर switch करें' : 'Switch to dark theme')}
                    >
                        {isDark ? <Sun className='w-4 h-4' /> : <Moon className='w-4 h-4' />}
                    </button>
                    {/* Close (mobile only) */}
                    <button
                        className='lg:hidden p-1 rounded-md flex-shrink-0 text-slate-500 hover:text-slate-900 hover:bg-orange-100'
                        onClick={() => setOpen(false)}
                        data-testid='admin-sidebar-close'
                        aria-label='Close menu'
                    >
                        <X className='w-4 h-4' />
                    </button>
                </div>

                {/* Search */}
                <div className='px-3 pt-3'>
                    <div className='relative'>
                        <Search className={cn('w-3.5 h-3.5 absolute left-2.5 top-2.5', isDark ? 'text-white/40' : 'text-slate-400')} />
                        <input
                            ref={searchRef}
                            value={searchQ}
                            onChange={(e) => setSearchQ(e.target.value)}
                            onKeyDown={(e) => {
                                // Phase 27 — Enter on a staff-code pattern (UPP-VEN-0002) jumps
                                // straight to the Staff page with the code pre-filtered.
                                if (e.key === 'Enter') {
                                    const q = (searchQ || '').trim();
                                    if (/^upp-[a-z]{3}-\d{3,6}$/i.test(q)) {
                                        nav(`/admin/staff?q=${encodeURIComponent(q.toUpperCase())}`);
                                        setSearchQ('');
                                        setOpen(false);
                                        e.preventDefault();
                                    }
                                }
                            }}
                            placeholder={lang === 'hi' ? 'खोजें… (UPP-VEN-0001 भी)' : 'Search nav or staff-code (UPP-VEN-0001)'}
                            className={cn('w-full pl-8 pr-10 py-2 rounded-lg text-[12.5px] outline-none focus:ring-2 focus:ring-orange-400/40', T.text, T.placeholder)}
                            style={{ background: T.inputBg, border: `1px solid ${T.inputBorder}` }}
                            data-testid='admin-sidebar-search'
                        />
                        {searchQ ? (
                            <button
                                onClick={() => setSearchQ('')}
                                className={cn('absolute right-2 top-2', isDark ? 'text-white/50 hover:text-white' : 'text-slate-400 hover:text-slate-700')}
                                aria-label='Clear search'
                            >
                                <X className='w-3.5 h-3.5' />
                            </button>
                        ) : (
                            <span
                                className={cn('absolute right-2 top-1.5 text-[9px] px-1.5 py-0.5 rounded font-mono', T.kbdBorder, T.kbdText)}
                                style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,.10)' : 'rgba(30,46,122,.15)'}` }}
                            >
                                ⌘K
                            </span>
                        )}
                    </div>
                </div>

                {/* Nav */}
                <div className='flex-1 overflow-y-auto py-3'>
                    <GroupedNav
                        items={items}
                        query={searchQ}
                        onNavigate={() => setOpen(false)}
                        staleDeviceCount={staleDeviceCount}
                        lang={lang}
                        theme={T}
                        isDark={isDark}
                    />
                </div>

                {/* User / logout */}
                <div className='px-3 py-3 space-y-2' style={{ borderTop: `1px solid ${T.borderTop}` }}>
                    <div className={cn('flex items-center gap-2 p-2 rounded-xl', isDark ? 'hover:bg-white/5' : 'hover:bg-orange-100/60')}>
                        <div
                            className='w-9 h-9 rounded-full flex items-center justify-center font-bold text-[13px] flex-shrink-0'
                            style={{ background: 'linear-gradient(135deg,#F26522,#FBBF24)', color: '#1E2E7A' }}
                        >
                            {(user?.email || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <div className='flex-1 min-w-0'>
                            <div className={cn('text-[12px] font-semibold truncate', T.userLabel)}>{user?.email}</div>
                            <div className='text-[10px] truncate flex items-center gap-1.5' style={{ color: isDark ? '#FBBF24' : '#F26522' }}>
                                <span className='w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0'></span>
                                <span>{ROLE_LABELS[user?.role] || user?.role}</span>
                                {user?.staff_code && (
                                    <span
                                        className={cn(
                                            'ml-auto shrink-0 rounded font-mono font-semibold tracking-wide px-1 py-[1px] text-[9px]',
                                            isDark ? 'bg-white/10 text-white/90' : 'bg-orange-100 text-orange-800',
                                        )}
                                        data-testid='sidebar-staff-code'
                                        title='Your unique staff ID'
                                    >
                                        {user.staff_code}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        className={cn('w-full', T.logoutBtn)}
                        onClick={() => { logout(); nav('/'); }}
                        data-testid='admin-logout'
                    >
                        <LogOut className='w-3.5 h-3.5 mr-1' /> {t('logout')}
                    </Button>
                </div>
            </aside>

            {/* Main content */}
            <div className='flex-1 min-w-0 flex flex-col'>
                <BroadcastTicker />
                <header className='sticky top-0 z-30 bg-white/90 backdrop-blur border-b'>
                    <div className='px-4 py-3 flex items-center justify-between gap-3'>
                        <button
                            className='lg:hidden p-2 rounded-md hover:bg-slate-100'
                            onClick={() => setOpen(true)}
                            data-testid='admin-mobile-nav'
                            aria-label='Open menu'
                        >
                            <Menu className='w-5 h-5' />
                        </button>
                        <div className='hidden md:flex items-center gap-2 text-[12px] text-slate-500'>
                            <Sparkles className='w-3.5 h-3.5' style={{ color: '#F26522' }} />
                            <span>{lang === 'hi' ? 'नमस्ते' : 'Welcome back'}, </span>
                            <span className='font-semibold text-slate-900'>{(user?.email || '').split('@')[0]}</span>
                            <span className='hidden lg:inline text-slate-400'>·</span>
                            <span className='hidden lg:inline text-emerald-700 font-medium flex items-center gap-1'>
                                <TrendingUp className='w-3 h-3' /> All systems online
                            </span>
                        </div>
                        <div className='flex items-center gap-2 ml-auto'>
                            <LiveShareToggle />
                            <NotificationBell lang={lang} />
                            <LanguageToggle />
                            <UserMenu />
                        </div>
                    </div>
                </header>
                <main className={cn('p-3 sm:p-4 md:p-6 max-w-[1400px]', bottomNavEnabled ? 'pb-20 md:pb-6' : 'pb-6')}>
                    <Outlet />
                </main>
            </div>

            <SaathiOnboardingWizard />
            {bottomNavEnabled && <MobileBottomNav onOpenMore={() => setOpen(true)} />}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────────
// GroupedNav — 7 collapsible groups with fuzzy search filter.
// Preserves role-based filtering (upstream) + mobile-app stale-devices dot.
// ────────────────────────────────────────────────────────────────────────────
function GroupedNav({ items, query, onNavigate, staleDeviceCount, lang, theme, isDark }) {
    const T = theme || {};
    // key → item lookup for fast group assembly
    const itemMap = useMemo(() => {
        const m = {};
        items.forEach(it => { m[it.key] = it; });
        return m;
    }, [items]);

    // Persist per-group open state
    const [openMap, setOpenMap] = useState(() => {
        const out = {};
        NAV_GROUPS.forEach((g, i) => {
            try {
                const v = localStorage.getItem(`nav2_grp_${g.id}`);
                out[g.id] = v !== null ? v === '1' : (i < 3);  // first 3 groups open by default
            } catch { out[g.id] = i < 3; }
        });
        return out;
    });
    const toggle = (gid, v) => {
        setOpenMap(prev => ({ ...prev, [gid]: v }));
        try { localStorage.setItem(`nav2_grp_${gid}`, v ? '1' : '0'); } catch { /* ignore */ }
    };

    // Apply fuzzy search
    const q = (query || '').trim().toLowerCase();
    const matchesQuery = (item) => {
        if (!q) return true;
        return (item.label || '').toLowerCase().includes(q) || (item.key || '').toLowerCase().includes(q);
    };

    // Build grouped item lists respecting role-filtering + query
    const grouped = useMemo(() => NAV_GROUPS.map(g => ({
        group: g,
        children: g.keys.map(k => itemMap[k]).filter(Boolean).filter(matchesQuery),
    })).filter(x => x.children.length > 0), [itemMap, q]);

    // Any items not covered by NAV_GROUPS? Show them as "Other".
    const grouppedKeys = new Set(NAV_GROUPS.flatMap(g => g.keys));
    const orphans = items.filter(it => !grouppedKeys.has(it.key) && matchesQuery(it));

    const renderItem = (item) => {
        const showStaleDot = item.key === 'mobile-app' && staleDeviceCount > 0;
        return (
            <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                onMouseEnter={() => prefetchRoute(item.to)}
                onFocus={() => prefetchRoute(item.to)}
                onTouchStart={() => prefetchRoute(item.to)}
                className={({ isActive }) => cn(
                    'relative flex items-center gap-2.5 pl-2.5 pr-2 py-1.5 rounded-lg text-[12.5px] mb-0.5 transition-colors',
                    isActive
                        ? cn('font-medium', T.text)
                        : cn(T.itemInactiveText, T.itemHover)
                )}
                style={({ isActive }) => isActive ? {
                    background: isDark
                        ? 'linear-gradient(90deg,rgba(242,101,34,.22),rgba(242,101,34,.02))'
                        : 'linear-gradient(90deg,rgba(242,101,34,.18),rgba(242,101,34,.02))',
                    borderLeft: '3px solid #F26522'
                } : {}}
                data-testid={`admin-nav-${item.key}`}
                end
            >
                {({ isActive }) => (
                    <>
                        <span
                            className='relative w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0'
                            style={isActive ? {
                                background: 'linear-gradient(135deg,#F26522,#FBBF24)',
                                color: '#1E2E7A'
                            } : {
                                background: T.itemIconBg,
                                color: T.itemIconColor
                            }}
                        >
                            <item.icon className='w-3.5 h-3.5' />
                            {showStaleDot && (
                                <span className='absolute -top-1 -right-1 flex h-2 w-2'
                                      title={`${staleDeviceCount} device${staleDeviceCount > 1 ? 's' : ''} on outdated version`}
                                      data-testid='mobile-app-stale-dot'>
                                    <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75'></span>
                                    <span className='relative inline-flex rounded-full h-2 w-2 bg-red-500'></span>
                                </span>
                            )}
                        </span>
                        <span className='flex-1 truncate'>{item.label}</span>
                        {showStaleDot && (
                            <span
                                className='inline-flex items-center rounded-full text-[10px] font-bold px-1.5 py-0'
                                style={{ background: 'linear-gradient(135deg,#F26522,#FF7F45)', color: '#fff' }}
                                data-testid='mobile-app-stale-count'
                            >
                                {staleDeviceCount}
                            </span>
                        )}
                    </>
                )}
            </NavLink>
        );
    };

    const renderGroup = ({ group, children }) => {
        const isOpen = q ? true : openMap[group.id];
        const Icon = group.icon;
        return (
            <div key={group.id} className='px-3 mb-1.5' data-testid={`admin-nav-group-${group.id}`}>
                <button
                    type='button'
                    onClick={() => !q && toggle(group.id, !isOpen)}
                    className={cn('w-full flex items-center gap-2 px-1.5 py-1.5 rounded-md transition-colors', T.groupLabelText, T.groupLabelHover)}
                    data-testid={`admin-nav-${group.id}-toggle`}
                    aria-expanded={isOpen}
                >
                    <span
                        className='w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0'
                        style={{ background: T.groupIconBg, color: T.groupIconColor }}
                    >
                        <Icon className='w-3.5 h-3.5' />
                    </span>
                    <span className='flex-1 text-left text-[10.5px] uppercase tracking-widest font-semibold'>
                        {lang === 'hi' && group.labelHi ? group.labelHi : group.label}
                    </span>
                    <span className='text-[10px] font-semibold opacity-70 px-1'>{children.length}</span>
                    {isOpen
                        ? <ChevronDown className={cn('w-3.5 h-3.5', T.chevron)} />
                        : <ChevronRight className={cn('w-3.5 h-3.5', T.chevron)} />}
                </button>
                {isOpen && (
                    <div className='mt-0.5' data-testid={`admin-nav-${group.id}-children`}>
                        {children.map(renderItem)}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className='space-y-1'>
            {grouped.map(renderGroup)}
            {orphans.length > 0 && (
                <div className='px-3 mb-1.5'>
                    <div className={cn('px-1.5 py-1.5 text-[10.5px] uppercase tracking-widest font-semibold', T.groupLabelText)}>
                        {lang === 'hi' ? 'अन्य' : 'Other'}
                    </div>
                    <div>{orphans.map(renderItem)}</div>
                </div>
            )}
            {grouped.length === 0 && orphans.length === 0 && q && (
                <div className={cn('px-4 py-8 text-center text-[12px]', T.textSubtle)}>
                    <Search className='w-4 h-4 mx-auto mb-2 opacity-60' />
                    {lang === 'hi' ? `"${q}" के लिए कुछ नहीं मिला` : `No results for "${q}"`}
                </div>
            )}
        </div>
    );
}
